import { useState, useEffect } from 'react';

const CONSENT_KEY = 'ltv_cookie_consent';

export function getCookieConsent() {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Hide the static HTML cookie banner once React takes over
    const htmlBanner = document.getElementById('ltv-cookie-banner');
    if (htmlBanner) htmlBanner.style.display = 'none';

    const consent = getCookieConsent();
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    } else {
      applyConsent(consent);
    }
  }, []);

  const applyConsent = (consent) => {
    if (window.gtag) {
      window.gtag('consent', 'update', {
        ad_storage: consent.ads ? 'granted' : 'denied',
        ad_user_data: consent.ads ? 'granted' : 'denied',
        ad_personalization: consent.ads ? 'granted' : 'denied',
        analytics_storage: consent.analytics ? 'granted' : 'denied',
      });
    }
  };

  const saveConsent = (consent) => {
    const data = { ...consent, timestamp: new Date().toISOString() };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(data));
    applyConsent(data);
    setVisible(false);
  };

  const acceptAll = () => saveConsent({ ads: true, analytics: true, functional: true });
  const rejectNonEssential = () => saveConsent({ ads: false, analytics: false, functional: true });

  if (!visible) return null;

  return (
    <div data-testid="cookie-consent-banner" style={styles.overlay}>
      <div style={styles.banner}>
        <div style={styles.content}>
          <h3 style={styles.title}>We value your privacy</h3>
          <p style={styles.text}>
            We use cookies to enhance your browsing experience, serve personalised ads and content,
            and analyse our traffic. By clicking "Accept All", you consent to our use of cookies.
          </p>

          {showDetails && (
            <div style={styles.details}>
              <div style={styles.category}>
                <strong style={styles.catTitle}>Essential Cookies</strong>
                <span style={styles.always}>Always active</span>
                <p style={styles.catDesc}>Required for the website to function. Cannot be disabled.</p>
              </div>
              <div style={styles.category}>
                <strong style={styles.catTitle}>Advertising Cookies</strong>
                <p style={styles.catDesc}>Used by Google AdSense to show personalised ads based on your browsing activity.</p>
              </div>
              <div style={styles.category}>
                <strong style={styles.catTitle}>Analytics Cookies</strong>
                <p style={styles.catDesc}>Help us understand how visitors interact with our website to improve the experience.</p>
              </div>
            </div>
          )}

          <div style={styles.links}>
            <button
              onClick={() => setShowDetails(!showDetails)}
              style={styles.detailsBtn}
              data-testid="cookie-details-toggle"
            >
              {showDetails ? 'Hide Details' : 'Cookie Details'}
            </button>
            <a href="/cookie-policy" style={styles.policyLink}>Cookie Policy</a>
          </div>
        </div>

        <div style={styles.actions}>
          <button
            data-testid="cookie-reject-btn"
            onClick={rejectNonEssential}
            style={styles.rejectBtn}
          >
            Reject Non-Essential
          </button>
          <button
            data-testid="cookie-accept-btn"
            onClick={acceptAll}
            style={styles.acceptBtn}
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 99998,
    padding: '16px',
    pointerEvents: 'none',
  },
  banner: {
    pointerEvents: 'auto',
    maxWidth: '640px',
    margin: '0 auto',
    background: '#1a1a1a',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 -4px 30px rgba(0,0,0,0.5)',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  content: {
    marginBottom: '16px',
  },
  title: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: 600,
    margin: '0 0 8px 0',
  },
  text: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '13px',
    lineHeight: 1.6,
    margin: 0,
  },
  details: {
    marginTop: '16px',
    borderTop: '1px solid rgba(255,255,255,0.08)',
    paddingTop: '12px',
  },
  category: {
    marginBottom: '12px',
  },
  catTitle: {
    color: '#ffffff',
    fontSize: '13px',
    display: 'inline',
  },
  always: {
    color: '#22c55e',
    fontSize: '11px',
    marginLeft: '8px',
    fontWeight: 500,
  },
  catDesc: {
    color: 'rgba(255,255,255,0.45)',
    fontSize: '12px',
    margin: '2px 0 0 0',
    lineHeight: 1.5,
  },
  links: {
    marginTop: '12px',
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
  },
  detailsBtn: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.5)',
    fontSize: '12px',
    cursor: 'pointer',
    padding: 0,
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
  },
  policyLink: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: '12px',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
  },
  actions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
  },
  rejectBtn: {
    padding: '10px 18px',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.15)',
    background: 'transparent',
    color: 'rgba(255,255,255,0.7)',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  acceptBtn: {
    padding: '10px 18px',
    borderRadius: '8px',
    border: 'none',
    background: '#2563eb',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
  },
};
